// src/components/Admin/NuevoColegiadoDialog.jsx
import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Checkbox,
  MenuItem,
} from "@mui/material";
import { Alert } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import axios from "axios";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { saveAs } from "file-saver";
import SelectEstado from "../common/Estados";

import { obtenerBICdesdeBanco } from "../common/Bics";
import { validarIBAN } from "../Utiles/Verificar/iban";
import { validarDNI } from "../Utiles/Verificar/dni";
import { validarEmail } from "../Utiles/Verificar/email";

// ======================= Utils locales =======================
const API = process.env.REACT_APP_API_URL;
const token = localStorage.getItem("token");
const headers = { Authorization: `Bearer ${token}` };

const schemaNuevo = Yup.object({
  nombre: Yup.string().trim().min(2, "Mínimo 2 caracteres").required("Obligatorio"),
  apellido_1: Yup.string().trim().nullable(),
  apellido_2: Yup.string().trim().nullable(),
  nif: Yup.string().trim().nullable(),
  email: Yup.string().trim().email("Email no válido").required("Obligatorio"),
  password: Yup.string().min(8, "Mínimo 8 caracteres").required("Obligatorio"),
  fecha_nacimiento_dmy: Yup.string().trim().nullable(),
});

const dmyToIso = (dmy) => {
  if (!dmy) return "";
  const m = String(dmy).trim().match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (!m) return "";
  const dd = m[1].padStart(2, "0");
  const mm = m[2].padStart(2, "0");
  const yyyy = m[3];
  return `${yyyy}-${mm}-${dd}`;
};

const getPageMeta = (data) => ({
  content: Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [],
  totalElements:
    typeof data?.totalElements === "number"
      ? data.totalElements
      : typeof data?.total_elements === "number"
        ? data.total_elements
        : 0,
  number: typeof data?.number === "number" ? data.number : typeof data?.page === "number" ? data.page : 0,
  size: typeof data?.size === "number" ? data.size : typeof data?.page_size === "number" ? data.page_size : 0,
});

const esVentanilla = (v) => (v || "").toUpperCase() === "VENTANILLA";

// Desglosa IBAN (ESkk + 20 CCC)
const extraerDatosIban = (iban) => {
  const limpio = (iban || "").replace(/\s+/g, "").toUpperCase();
  const banco = limpio.slice(4, 8);
  const sucursal_Bancaria = limpio.slice(8, 12);
  const digito_Control = limpio.slice(12, 14);
  const cuenta_Bancaria = limpio.slice(14, 24); // 10 dígitos
  return { banco, sucursal_Bancaria, digito_Control, cuenta_Bancaria, ibanLimpio: limpio };
};

// Etiquetas y nombres de archivos
const etiquetasArchivos = {
  solicitud: "Solicitud de Alta Firmada Electrónicamente (PDF)",
  dni: "DNI/NIE",
  titulo: "Título de Enfermería o Certificación Supletoria Provisional",
  certificacion: "Certificación académica de notas",
  autorizacion: "Autorización para consultar Registro Nacional de Títulos Universitarios (PDF)",
  pago: "Justificante de pago",
  sepa: "Autorización de domiciliación bancaria (PDF)",
  foto: "Foto tamaño carnet",
};
const nombreArchivos = {
  solicitud: "solicitud_alta",
  dni: "dni_nie",
  titulo: "titulo_enfermeria",
  certificacion: "certificado_notas",
  autorizacion: "autorizacion_consulta_titulos",
  pago: "justificante_pago",
  sepa: "sepa",
  foto: "foto",
};

// === CARGA DE PDF SIN CORS (mismo origen) + verificación de tipo ===
async function loadPdfFromFront(pathRel) {
  const url = new URL(pathRel, window.location.origin).href; // p.ej. "/SEPA.pdf"
  const res = await fetch(url, { cache: "no-store" });
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!res.ok || !ct.includes("application/pdf")) {
    throw new Error(`No es un PDF válido: url=${url} status=${res.status} content-type=${ct}`);
  }
  return res.arrayBuffer();
}

// Buscar código colegiado por NIF
async function buscarCodigoPorNif(nif) {
  const clean = (nif || "").toString().trim().toUpperCase();
  if (!clean) return null;
  const params = { page: 0, size: 1, sortBy: "codigo", order: "asc", nif: clean };
  const { data } = await axios.get(`${API}/api/admin/buscar`, { headers, params });
  const meta = getPageMeta(data);
  const arr = Array.isArray(meta.content) ? meta.content : [];
  const match = arr.find((r) => (r?.nif || "").toString().toUpperCase() === clean);
  return match?.codigo ?? null;
}

// Sube TODOS los archivos seleccionados al área del colegiado (ADMIN)
async function subirDocumentosTrasAlta(colegiadoCodigo, archivosMapa) {
  for (const [clave, file] of Object.entries(archivosMapa || {})) {
    if (!file) continue;
    const ext = (file.name?.split(".").pop() || "pdf").toLowerCase();
    const nombreDeseado = `${nombreArchivos[clave]}.${ext}`;

    const fd = new FormData();
    fd.append("file", file, nombreDeseado);
    fd.append("colegiadoId", String(colegiadoCodigo));

    await axios.post(`${API}/api/documentos/admin/subir`, fd, {
      headers: { ...headers, "Content-Type": "multipart/form-data" },
    });
  }
}

// Crear Mandato TMP público (idempotente por (nif, iban))
async function crearMandatoTmpPublico({ colegiadoCodigo = null, nif, iban, bic, fechaFirma, docSepaId = null }) {
  const body = { colegiadoCodigo, nif, iban, bic, fechaFirma, docSepaId };
  const { data } = await axios.post(`${API}/api/mandatos/tmp/publico`, body, { headers });
  const mndtId = data?.mandatoId ?? data?.mndtId ?? data?.mndt_id ?? null;
  return { mndtId, raw: data };
}

// Preview de mndt_id (no guarda)
async function previewMandatoId(nif, iban, bic) {
  try {
    const { data } = await axios.get(`${API}/api/mandatos/preview`, {
      params: { nif, iban, bic },
      headers,
    });
    return data?.mandatoId || null;
  } catch {
    return null;
  }
}

// ======================= PDFs =======================
async function pdfSolicitud(values) {
  const existingPdfBytes = await loadPdfFromFront("/Solicitud_ingreso.pdf");
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const firstPage = pdfDoc.getPages()[0];
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const draw = (t, x, y, size = 10) =>
    t != null && firstPage.drawText(String(t), { x, y, size, font, color: rgb(0, 0, 0) });

  const hoy = new Date();
  const meses = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
  ];

  // Nombre y apellidos
  draw(`${values.nombre || ""} ${values.apellido_1 || ""} ${values.apellido_2 || ""}`.trim(), 112.07, 707);

  // Nacimiento
  draw(values.localidad_nacimiento || "", 123.56, 692);
  draw(values.provincia_nacimiento || values.provincia || "", 305.12, 692);
  draw(values.fecha_nacimiento_dmy || "", 469, 692);

  // Datos identificación y domicilio
  draw(values.nif || "", 140.2, 675);
  draw(values.direccion || "", 290.74, 675);
  draw(values.poblacion || "", 135, 659);
  draw(values.provincia || "", 380, 659);
  draw(values.cp || "", 95, 643);
  draw(values.telefono || "", 185, 642);
  draw(values.email || "", 322, 640);

  // Académicos y bancarios
  draw(values.universidad || values.titulacion || "", 273, 623);
  draw(values.ano_fin || "", 122, 606);
  draw(values.entidad_bancaria || "", 254, 568);
  draw(values.iban || "", 130, 552);

  // Fecha
  draw(String(hoy.getDate()), 314, 149);
  draw(meses[hoy.getMonth()], 353, 149);
  draw(String(hoy.getFullYear()), 487, 149);

  const pdfBytes = await pdfDoc.save();
  saveAs(new Blob([pdfBytes], { type: "application/pdf" }), "Solicitud_Ingreso.pdf");
}

async function pdfRegistro(values) {
  const existingPdfBytes = await loadPdfFromFront("/registro_nacional.pdf");
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const firstPage = pdfDoc.getPages()[0];
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const draw = (t, x, y, size = 12) =>
    t != null && firstPage.drawText(String(t), { x, y, size, font, color: rgb(0, 0, 0) });

  const hoy = new Date();
  const meses = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
  ];

  draw(`${values.nombre || ""} ${values.apellido_1 || ""} ${values.apellido_2 || ""}`.trim(), 140, 693);
  draw(values.nif || "", 140, 665);
  const direccion = `${values.direccion || ""}`.trim();
  const poblacion = values.poblacion ? `en ${values.poblacion}` : "";
  draw(`${direccion} ${poblacion}`.trim(), 160, 637);
  draw(values.telefono || "", 140, 610);
  draw(values.email || "", 200, 580);

  draw(String(hoy.getDate()), 340, 282);
  draw(meses[hoy.getMonth()], 395, 282);
  draw(String(hoy.getFullYear()).slice(-2), 522, 282);

  const pdfBytes = await pdfDoc.save();
  saveAs(new Blob([pdfBytes], { type: "application/pdf" }), "Registro_Nacional.pdf");
}

async function pdfSepa(values, mandatoIdParaPdf) {
  // Nombre exacto con mayúsculas
  const existingPdfBytes = await loadPdfFromFront("/SEPA.pdf");
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const firstPage = pdfDoc.getPages()[0];
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const draw = (t, x, y, size = 10) =>
    t != null && firstPage.drawText(String(t), { x, y, size, font, color: rgb(0, 0, 0) });

  const nombreCompleto = `${(values.nombre || "").toUpperCase()} ${(values.apellido_1 || "").toUpperCase()} ${(values.apellido_2 || "").toUpperCase()}`.trim();
  const linea1 = `${(values.direccion || "").toUpperCase()}`.trim();
  const linea2 = `${(values.cp || "").toUpperCase()} ${(values.poblacion || "").toUpperCase()} ${(values.provincia || "").toUpperCase()}`.trim();

  const { banco, ibanLimpio } = extraerDatosIban(values.iban || "");
  const bicFromMap = obtenerBICdesdeBanco(banco);
  const bic = (values.bic || bicFromMap || "").toUpperCase(); // editable

  // Cabecera derecha
  draw(nombreCompleto, 340, 760);
  draw(linea1, 340, 745);
  draw(linea2, 340, 730);
  draw((values.pais || "").toUpperCase(), 340, 715);
  draw(`Tel.: ${values.telefono || ""}`, 340, 700);
  draw(values.email || "", 340, 685);

  // mndt_id
  draw(`${mandatoIdParaPdf || "SIN ID DISPONIBLE"}`, 299, 604);

  // Bloque inferior / banco
  draw(nombreCompleto, 340, 300);
  draw(linea1, 340, 265);
  draw(linea2, 340, 250);
  draw((values.pais || "").toUpperCase(), 340, 235);
  draw(banco, 66, 185);
  draw(bic || "", 170, 185);
  draw(ibanLimpio, 300, 185);

  const hoy = dayjs();
  draw(hoy.format("DD/MM/YYYY"), 200, 134);

  const pdfBytes = await pdfDoc.save();
  saveAs(new Blob([pdfBytes], { type: "application/pdf" }), "SEPA.pdf");
}

// ======================= Componente principal =======================
export default function NuevoColegiadoDialog({
  open,
  onClose,
  fetchPage,
  page,
  pageSize,
  sortModel,
  filtersApplied,
  notify,
}) {
  const [archivosNuevo, setArchivosNuevo] = React.useState({});
  const [generando, setGenerando] = React.useState(false);
  const [formError, setFormError] = React.useState("");

  const handleFileChangeLocal = (e, nombreCampo) => {
    setArchivosNuevo((prev) => ({
      ...prev,
      [nombreCampo]: e.target.files[0],
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        setFormError("");
        onClose?.();
      }}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { width: "100%", maxWidth: 1100 },
      }}
    >
      <DialogTitle>Nuevo colegiado</DialogTitle>

      <Formik
        initialValues={{
          usarVentanilla: false,
          estadoId: "",
          nombre: "",
          apellido_1: "",
          apellido_2: "",
          nif: "",
          email: "",
          password: "",
          fecha_nacimiento_dmy: "",
          localidad_nacimiento: "",
          provincia_nacimiento: "",
          sexo: "",
          rol: "PENDIENTE",
          // contacto
          direccion: "",
          poblacion: "",
          provincia: "",
          pdestino: "",
          cp: "",
          pais: "ESPAÑA",
          telefono: "",
          porigen: "",
          // banco
          entidad_bancaria: "",
          iban: "",
          bic: "", // editable y autocalculado
          // académicos
          universidad: "",
          ano_fin: "",
          // otros
          observaciones: "",
        }}
        validationSchema={schemaNuevo}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          setFormError("");
          setGenerando(true);
          try {
            const nifNormalizado = (values.nif || "").trim().toUpperCase(); const validarDocumento = (documento) => {
              const letras = "TRWAGMYFPDXBNJZSQVHLCKE";
              const dni = /^(\d{8})([A-Z])$/.exec(documento); if (dni) { return letras[Number(dni[1]) % 23] === dni[2]; }
              const nie = /^([XYZ])(\d{7})([A-Z])$/.exec(documento); if (nie) { const prefijo = { X: "0", Y: "1", Z: "2" }[nie[1]]; const numero = Number(prefijo + nie[2]); return letras[numero % 23] === nie[3]; } return false;
            };
            const docOk = validarDocumento(nifNormalizado);
            const emailOk = validarEmail(values.email);
            const vent = esVentanilla(values.iban);
            const ibanOk = vent ? true : validarIBAN(values.iban);

            if (!docOk) throw new Error("Documento de identidad no válido");
            if (!emailOk) throw new Error("Email no válido");
            if (!ibanOk) throw new Error("IBAN no válido");

            // Desglose IBAN / BIC
            let banco = "",
              sucursal_Bancaria = "",
              digito_Control = "",
              cuenta_Bancaria = "";
            let ibanLimpio = "",
              bicCalculado = "";

            if (!vent) {
              const parts = extraerDatosIban(values.iban);
              banco = parts.banco;
              sucursal_Bancaria = parts.sucursal_Bancaria;
              digito_Control = parts.digito_Control;
              cuenta_Bancaria = parts.cuenta_Bancaria;
              ibanLimpio = parts.ibanLimpio;

              const bicFromMap = (obtenerBICdesdeBanco(banco) || "").toUpperCase();
              bicCalculado = (values.bic || bicFromMap || "").toUpperCase();
            } else {
              // Modo ventanilla
              ibanLimpio = "VENTANILLA";
              bicCalculado = "";
            }
            const fechaNacimientoIso = dmyToIso(values.fecha_nacimiento_dmy);

            const payload = {
              nombre: values.nombre?.trim(),
              apellido_1: values.apellido_1?.trim() || "",
              apellido_2: values.apellido_2?.trim() || "",
              nif: nifNormalizado,
              email: values.email?.trim(),
              password: values.password,
              fecha_Nacimiento: fechaNacimientoIso || "",
              rol: values.rol,
              estadoId: values.estadoId ? Number(values.estadoId) : null,
              estado_id: values.estadoId ? Number(values.estadoId) : null,
              sexo: values.sexo || "",

              // contacto opcional
              telefono_1: values.telefono || "",
              direccion: (values.direccion || "").toUpperCase(),
              localidad: values.poblacion || "",
              provincia: values.provincia || "",
              c_p_: values.cp || "",
              pais: values.pais || "",
              localidad_nacimiento: values.localidad_nacimiento || "",
              provincia_nacimiento: values.provincia_nacimiento || "",
              porigen: values.porigen || "",
              pdestino: values.pdestino || "",
              observaciones: values.observaciones || "",

              remesable: 1,
              fecha_1a_colegiacion: dayjs().format("YYYY-MM-DD"),

              // bancario (desglosado)
              banco,
              sucursal_bancaria: sucursal_Bancaria,
              cuenta_bancaria: cuenta_Bancaria,
              digito_control: digito_Control,
              iban: ibanLimpio,
              bic: bicCalculado,

              // académicos
              universidad: (values.universidad || "").toUpperCase(),
              ano_fin: values.ano_fin || "",
            };

            const cre = await axios.post(`${API}/api/admin/colegiados`, payload, { headers });

            let nuevoId =
              cre?.data?.codigo ?? cre?.data?.Codigo ?? cre?.data?.id ?? cre?.data?.colegiadoId ?? null;

            if (!nuevoId) {
              const nifClean = (values.nif || "").toString().trim().toUpperCase();
              const params = { page: 0, size: 1, sortBy: "codigo", order: "asc", nif: nifClean };
              try {
                const resBusq = await axios.get(`${API}/api/admin/buscar`, { headers, params });
                const meta = getPageMeta(resBusq.data);
                const match = (meta.content || []).find(
                  (r) => (r?.nif || "").toString().toUpperCase() === nifClean
                );
                nuevoId = match?.codigo ?? null;
              } catch (_) { }
            }

            if (!nuevoId) {
              throw new Error("No se recibió el ID/código del colegiado creado");
            }

            // Subida de docs
            try {
              await subirDocumentosTrasAlta(nuevoId, archivosNuevo, headers);
              try {
                await axios.post(
                  `${API}/api/documentos/asignar-foto-admin`,
                  { colegiadoId: nuevoId },
                  { headers }
                );
              } catch (eFoto) {
                console.warn("No se pudo asignar foto:", eFoto?.message || eFoto);
              }
            } catch (eUp) {
              console.error("Fallo subiendo documentos:", eUp);
            }

            // Mandato TMP (bind + crear idempotente)
            let sepaDocumentId = null;
            try {
              const { data: docs } = await axios.get(`${API}/api/documentos/listar`, {
                params: { colegiadoId: nuevoId },
                headers,
              });
              const sepaDoc = (docs || []).find((d) =>
                ((d.nombre || d.filename || "") + "").toLowerCase().includes("sepa")
              );
              sepaDocumentId = sepaDoc?.id ?? null;
            } catch { }

            try {
              await axios.post(
                `${API}/api/mandatos/tmp/bind`,
                { colegiadoCodigo: nuevoId, nif: values.nif, iban: ibanLimpio, docSepaId: sepaDocumentId },
                { headers }
              );
            } catch { }

            try {
              await crearMandatoTmpPublico({
                colegiadoCodigo: nuevoId,
                nif: values.nif,
                iban: ibanLimpio,
                bic: bicCalculado,
                fechaFirma: dayjs().format("YYYY-MM-DD"),
                docSepaId: sepaDocumentId,
              });
            } catch { }

            notify?.(`Colegiado creado correctamente (código ${nuevoId}).`);
            resetForm();
            setArchivosNuevo({});
            onClose?.();
            fetchPage?.(0, pageSize, sortModel, filtersApplied);
          } catch (e) {
            console.error(e);
            const raw = e?.response?.data ?? e?.message ?? e;
            const msg =
              typeof raw === "string"
                ? raw
                : raw?.message ||
                Object.entries(raw || {})
                  .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`)
                  .join(" · ") ||
                "No se pudo crear el colegiado";
            setFormError(msg);
          } finally {
            setSubmitting(false);
            setGenerando(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

              <Box sx={{ mb: 2 }}>
                <SelectEstado
                  name="estadoId"
                  label="Estado"
                  value={values.estadoId}
                  onChange={(e) => {
                    const v = e?.target?.value;
                    const n = v === "" || v == null ? "" : Number(v);
                    setFieldValue("estadoId", Number.isNaN(n) ? "" : n);
                  }}
                />
              </Box>

              {/* DATOS BÁSICOS */}
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                <TextField name="porigen" label="Provincia origen" value={values.porigen} onChange={handleChange} />
                <TextField name="pdestino" label="Provincia destino" value={values.pdestino} onChange={handleChange} />
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  fullWidth
                />
                <TextField
                  name="password"
                  label="Password"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  fullWidth
                />
                <TextField
                  name="nombre"
                  label="Nombre"
                  value={values.nombre}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.nombre && Boolean(errors.nombre)}
                  helperText={touched.nombre && errors.nombre}
                  fullWidth
                />
                <TextField name="apellido_1" label="Apellido 1" value={values.apellido_1} onChange={handleChange} />
                <TextField name="apellido_2" label="Apellido 2" value={values.apellido_2} onChange={handleChange} />
                <TextField name="nif" label="DNI/NIE/Pasaporte" value={values.nif} onChange={handleChange} />
                <TextField
                  select
                  name="sexo"
                  label="Sexo"
                  value={values.sexo ?? ""}
                  onChange={handleChange}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="0">Masculino</MenuItem>
                  <MenuItem value="1">Femenino</MenuItem>
                </TextField>
                <TextField
                  name="fecha_nacimiento_dmy"
                  label="Fecha nacimiento (dd-mm-yyyy)"
                  value={values.fecha_nacimiento_dmy}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  placeholder="dd-mm-yyyy"
                />
                <TextField
                  name="localidad_nacimiento"
                  label="Localidad nacimiento"
                  value={values.localidad_nacimiento}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                />
                <TextField
                  name="provincia_nacimiento"
                  label="Provincia nacimiento"
                  value={values.provincia_nacimiento}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                />
              </Box>

              {/* CONTACTO */}
              <Typography variant="h6" sx={{ mt: 3 }}>
                Contacto
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "repeat(3, 1fr)" }} gap={2}>
                <TextField name="telefono" label="Teléfono" value={values.telefono} onChange={handleChange} />
                <TextField name="pais" label="País" value={values.pais} onChange={handleChange} />
                <TextField name="provincia" label="Provincia" value={values.provincia} onChange={handleChange} />
                <TextField name="poblacion" label="Población" value={values.poblacion} onChange={handleChange} />
                <TextField
                  name="direccion"
                  label="Dirección (calle, nº, piso, etc.)"
                  value={values.direccion}
                  onChange={handleChange}
                />
                <TextField name="cp" label="Código Postal" value={values.cp} onChange={handleChange} />
              </Box>

              {/* ACADÉMICOS */}
              <Typography variant="h6" sx={{ mt: 3 }}>
                Datos académicos
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                <TextField name="universidad" label="Universidad" value={values.universidad} onChange={handleChange} />
                <TextField name="ano_fin" label="Año fin" value={values.ano_fin} onChange={handleChange} />
              </Box>

              {/* BANCARIOS */}
              <Typography variant="h6" sx={{ mt: 3 }}>
                Datos bancarios
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr" }} gap={2}>
                <TextField
                  name="entidad_bancaria"
                  label="Entidad Bancaria"
                  value={values.entidad_bancaria}
                  onChange={handleChange}
                />

                {/* IBAN */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <TextField
                    name="iban"
                    label="IBAN (sin espacios)"
                    value={values.iban}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\s+/g, "").toUpperCase();
                      // set IBAN
                      handleChange({ target: { name: "iban", value: val } });

                      // autocalcular BIC SOLO desde mapa local
                      if (!esVentanilla(val) && /^ES\d{22}$/i.test(val)) {
                        const { banco } = extraerDatosIban(val);
                        const found = (obtenerBICdesdeBanco(banco) || "").toUpperCase();
                        setFieldValue("bic", found && found !== "DESCONOCIDO" ? found : "");
                      } else if (esVentanilla(val)) {
                        setFieldValue("bic", "");
                      }
                    }}
                    disabled={values.usarVentanilla}
                    onBlur={handleBlur}
                    helperText={
                      esVentanilla(values.iban)
                        ? "Modo VENTANILLA activo: se omitirá la validación de IBAN."
                        : "Se autocalculará el BIC al introducir un IBAN válido. Si no se encuentra, introdúcelo manualmente."
                    }
                    fullWidth
                  />

                  <Box sx={{ whiteSpace: "nowrap" }}>
                    <Checkbox
                      checked={!!values.usarVentanilla}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        handleChange({ target: { name: "usarVentanilla", value: checked } });
                        handleChange({ target: { name: "iban", value: checked ? "VENTANILLA" : "" } });
                        setFieldValue("bic", ""); // limpiar BIC en ventanilla
                      }}
                    />
                    <Typography variant="body2" component="span">
                      Sin IBAN (VENTANILLA)
                    </Typography>
                  </Box>
                </Box>

                {/* BIC (editable) */}
                <TextField
                  name="bic"
                  label="BIC"
                  value={values.bic}
                  onChange={(e) => {
                    // Permitir edición manual libre
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  placeholder="Se autocalculará cuando se introduzca el IBAN. Si no se encuentra, introduce el BIC manualmente."
                  helperText="Editable. Si el IBAN cambia y el banco está en el mapa local, se autocompletará."
                  fullWidth
                />
              </Box>

              {/* GENERACIÓN DE PDFs */}
              <Box mt={3} display="flex" gap={2} flexWrap="wrap">
                <Button variant="outlined" onClick={() => pdfSolicitud(values)} disabled={generando}>
                  PDF Solicitud de Alta
                </Button>
                <Button variant="outlined" onClick={() => pdfRegistro(values)} disabled={generando}>
                  PDF Autorización Registro Nacional
                </Button>

                <Button
                  variant="outlined"
                  disabled={generando || esVentanilla(values.iban)}
                  onClick={async () => {
                    try {
                      setGenerando(true);
                      const { banco, ibanLimpio } = extraerDatosIban(values.iban || "");
                      const bicFromMap = (obtenerBICdesdeBanco(banco) || "").toUpperCase();
                      const bic = (values.bic || bicFromMap || "").toUpperCase();

                      let mandatoIdParaPdf = null;

                      // 1) Intentar crear TMP siempre (colegiadoCodigo=null)
                      try {
                        const { mndtId } = await crearMandatoTmpPublico({
                          colegiadoCodigo: null,
                          nif: values.nif,
                          iban: ibanLimpio,
                          bic,
                          fechaFirma: dayjs().format("YYYY-MM-DD"),
                          docSepaId: null,
                        });
                        mandatoIdParaPdf = mndtId || null;
                      } catch (e) {
                        // 2) Preview si falla
                        try {
                          mandatoIdParaPdf = await previewMandatoId(values.nif, ibanLimpio, bic);
                        } catch { }
                      }

                      // 3) Red de seguridad
                      if (!mandatoIdParaPdf) {
                        try {
                          mandatoIdParaPdf = await previewMandatoId(values.nif, ibanLimpio, bic);
                        } catch { }
                      }

                      // 4) Fallback corto
                      if (!mandatoIdParaPdf) {
                        const fecha = dayjs().format("YYYYMMDD");
                        const nifNorm = (values.nif || "X").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
                        const tailIban = (ibanLimpio || "").slice(-10) || "IBAN000000";
                        mandatoIdParaPdf = `MND${fecha}${nifNorm}${tailIban}`.slice(0, 35);
                      }

                      await pdfSepa(values, mandatoIdParaPdf);
                    } finally {
                      setGenerando(false);
                    }
                  }}
                >
                  PDF Autorización SEPA
                </Button>
              </Box>

              {/* SUBIDA DE FICHEROS */}
              <Typography variant="h6" sx={{ mt: 3 }}>
                Subir documentos
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={1}>
                {[
                  { name: "solicitud", label: etiquetasArchivos.solicitud },
                  { name: "dni", label: etiquetasArchivos.dni },
                  { name: "titulo", label: etiquetasArchivos.titulo },
                  { name: "certificacion", label: etiquetasArchivos.certificacion },
                  { name: "autorizacion", label: etiquetasArchivos.autorizacion },
                  { name: "pago", label: etiquetasArchivos.pago },
                  { name: "sepa", label: etiquetasArchivos.sepa },
                  { name: "foto", label: etiquetasArchivos.foto },
                ].map(({ name, label }) => (
                  <Box key={name}>
                    <Typography sx={{ mb: 0.5 }}>{label}</Typography>
                    <input type="file" name={name} onChange={(e) => handleFileChangeLocal(e, name)} />
                  </Box>
                ))}
              </Box>

              {/* OBSERVACIONES */}
              <Typography variant="h6" sx={{ mt: 3 }}>
                Observaciones
              </Typography>
              <TextField
                name="observaciones"
                label="Observaciones"
                value={values.observaciones}
                onChange={handleChange}
                multiline
                rows={3}
                fullWidth
                sx={{ mt: 1 }}
              />
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  setFormError("");
                  onClose?.();
                }}
                disabled={isSubmitting || generando}
              >
                Cancelar
              </Button>

              <Button type="submit" variant="contained" disabled={isSubmitting || generando}>
                {isSubmitting || generando ? "Procesando..." : "Crear"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
